/**
 * useChatNavigation Hook - Chat-MFE Navigation Integration
 *
 * This hook handles navigation messages from the chat-mfe iframe,
 * enabling seamless navigation without opening new tabs.
 *
 * @usage
 * ```javascript
 * import { useChatNavigation } from './useChatNavigation';
 * import { useHistory } from 'react-router-dom';
 *
 * function ChatPopup() {
 *   const history = useHistory();
 *
 *   const { iframeRef } = useChatNavigation({
 *     chatOrigin: process.env.REACT_APP_CHAT_WIDGET_URL,
 *     onNavigate: (url) => history.push(url),
 *     onCloseChat: () => setIsOpen(false)
 *   });
 *
 *   return <iframe ref={iframeRef} src="..." />;
 * }
 * ```
 */

import { useEffect, useRef } from 'react';

/**
 * Hook to handle chat-mfe navigation requests
 *
 * @param {Object} config - Configuration object
 * @param {string} config.chatOrigin - The origin of the chat-mfe (e.g., 'https://chat-mfe.example.com')
 * @param {Function} config.onNavigate - Callback to handle navigation (use your router)
 * @param {Function} [config.onCloseChat] - Optional callback to close/hide the chat
 * @param {Function} [config.onNavigationEvent] - Optional callback for analytics/logging
 * @returns {Object} - { iframeRef } - Ref to attach to iframe element
 */
export function useChatNavigation(config) {
  const { chatOrigin, onNavigate, onCloseChat, onNavigationEvent } = config;
  const iframeRef = useRef(null);

  useEffect(() => {
    /**
     * Handle messages from chat-mfe iframe
     */
    const handleMessage = (event) => {
      // Security: Validate origin
      // In development, we check if origin starts with the base URL
      const chatBaseUrl = chatOrigin || process.env.REACT_APP_CHAT_WIDGET_URL;


      // Handle navigation request
      if (event.data?.type === 'CHAT_MFE_NAVIGATE') {
        const { url, target, closeChat, context } = event.data.payload;

        // Analytics/logging
        if (onNavigationEvent) {
          onNavigationEvent({ url, target, closeChat, context });
        }

        // Handle navigation based on target
        if (target === '_top') {
          // Extract pathname from full URL if needed
          // React Router expects relative paths like "/l/listing/id"
          let navigationPath = url;

          try {
            // If url is a full URL (http://...), extract pathname
            if (url.startsWith('http://') || url.startsWith('https://')) {
              const urlObj = new URL(url);
              navigationPath = urlObj.pathname + urlObj.search + urlObj.hash;
            }
          } catch (e) {
            // If URL parsing fails, use as-is (likely already a relative path)
            console.warn('[useChatNavigation] Could not parse URL, using as-is:', url);
          }

          // SPA navigation in same tab
          onNavigate(navigationPath);

          // Close chat if requested
          if (closeChat && onCloseChat) {
            onCloseChat();
          }
        } else if (target === '_blank') {
          // Open in new tab
          // Note: chat-mfe will also handle this, but we track it here
          window.open(url, '_blank', 'noopener,noreferrer');

          // Chat remains open (closeChat should be false for _blank targets)
        }
      }
    };

    // Listen for messages from chat-mfe
    window.addEventListener('message', handleMessage);

    /**
     * Send handshake to chat-mfe when iframe loads
     * This tells the chat-mfe that parent is ready to handle navigation
     */
    const iframe = iframeRef.current;
    if (iframe) {
      const handleIframeLoad = () => {
        const chatBaseUrl = chatOrigin || process.env.REACT_APP_CHAT_WIDGET_URL;

        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            { type: 'PARENT_READY' },
            chatBaseUrl || '*'
          );
        }
      };

      iframe.addEventListener('load', handleIframeLoad);

      // Cleanup
      return () => {
        window.removeEventListener('message', handleMessage);
        iframe.removeEventListener('load', handleIframeLoad);
      };
    }

    // Cleanup if no iframe
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [chatOrigin, onNavigate, onCloseChat, onNavigationEvent]);

  return {
    /**
     * Ref to attach to the iframe element
     * Required for sending the PARENT_READY handshake
     */
    iframeRef
  };
}
